import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { $generateNodesFromDOM } from '@lexical/html';
import { $generateHtmlFromNodes } from '@lexical/html';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { Transformer } from '@lexical/markdown';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { Box, SxProps, Theme } from '@mui/material';
import {
  $getRoot,
  EditorState,
  Klass,
  LexicalEditor,
  LexicalNode,
} from 'lexical';
import {
  ComponentType,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLatest } from 'react-use';

import { diagnostics } from '@/utils/diagnostics';
import { UnreachableError } from '@/utils/errors';

import { useTextEditorContext } from './contexts/textEditor.context';
import { useTextEditorTheme } from './hooks/useTextEditorTheme';
import { CheckListCaretFixPlugin } from './plugins/CheckListCaretFixPlugin/CheckListCaretFixPlugin';
import { CitationPastePlugin } from './plugins/CitationPastePlugin/CitationPastePlugin';
import { CitationNode } from './plugins/CitationPlugin/CitationNode';
import { CitationPlugin } from './plugins/CitationPlugin/CitationPlugin';
import { ContinueStylePlugin } from './plugins/ContinueStylePlugin/ContinueStylePlugin';
import { CopyAllPlugin } from './plugins/CopyAllPlugin/CopyAllPlugin';
import { FontSizeNode } from './plugins/FontSizePlugin/FontSizeNode';
import { FontSizePlugin } from './plugins/FontSizePlugin/FontSizePlugin';
import { HighlightNode, HighlightPlugin } from './plugins/HighlightPlugin';
import { InternalLinkNode } from './plugins/InternalLinkPlugin/InternalLinkNode';
import { MermaidNode } from './plugins/MermaidPlugin/MermaidNode';
import { MermaidPlugin } from './plugins/MermaidPlugin/MermaidPlugin';
import { RootSelectionTabPlugin } from './plugins/RootSelectionTabPlugin/RootSelectionTabPlugin';
import { TableRowClearPlugin } from './plugins/TableRowClearPlugin/TableRowClearPlugin';
import { ToolbarPlugin as DefaultToolbarPlugin } from './plugins/ToolbarPlugin/ToolbarPlugin';
import { Block } from './plugins/ToolbarPlugin/ToolbarPlugin.utils';
import {
  convertFromMarkdownString,
  convertToMarkdownString,
  EditorInner as DefaultEditorInner,
  EMPTY_EDITOR_JSON,
  TextEditorContentEditable,
  TextEditorWrapper as DefaultTextEditorWrapper,
} from './TextEditor.utils';
import { markdownTransformers } from './transformers';

export type TextEditorKind = 'markdown' | 'html' | 'json';
export interface TextEditorProps {
  initialValue: string;
  initialValueKind?: TextEditorKind;
  onUpdate?: (convertedEditorState: string) => void;
  /** Fires synchronously after Lexical serializes an editor update. Use this
   *  only when a later event, such as click-away, must read the latest value
   *  before React effects run. Unlike `onUpdate`, this does not fire on mount. */
  onImmediateUpdate?: (convertedEditorState: string) => void;
  readOnly?: boolean;
  autoFocus?: boolean;
  hideBorder?: boolean;
  ToolbarPlugin?: ComponentType<{
    textEditorKind: TextEditorKind;
    omitBlockTypes?: Block[];
    allowTableInsert?: boolean;
    showCompactBlockDropdown?: boolean;
  }>;
  TextEditorWrapper?: ComponentType<{
    children: ReactNode;
    hideBorder?: boolean;
  }>;
  EditorInner?: ComponentType<{ children: ReactNode; sx?: SxProps<Theme> }>;
  innerSx?: SxProps<Theme>;
  omitBlockTypes?: Block[];
  preserveNewLines?: boolean;
  defaultAutofocusSelection?: 'rootStart' | 'rootEnd';
  allowTableInsert?: boolean;
  showCompactBlockDropdown?: boolean;
  /** Extra Lexical node types to register with the editor. */
  additionalNodes?: Klass<LexicalNode>[];
  /** Extra markdown transformers, given priority over the defaults. They are
   *  prepended to the transformer list so element transformers (e.g. the
   *  horizontal rule) win over built-ins like the list transformer for
   *  ambiguous lines such as `- - -`. */
  additionalTransformers?: Transformer[];
  /** Extra plugins to render inside the LexicalComposer. */
  additionalPlugins?: ReactNode;
  /** Optional tenant-branded color applied to `p`, `h*`, and `li` via
   *  the Lexical theme classes. See `useTextEditorTheme`. */
  bodyTextColor?: string | null;
  /** Optional tenant-branded body alignment applied to `p`, `h*`, and
   *  `li` via the Lexical theme classes. Absent → left-aligned default.
   *  See `useTextEditorTheme`. */
  bodyTextAlignment?: 'left' | 'center' | 'right' | null;
}

export const TextEditor = ({
  initialValue,
  initialValueKind = 'markdown',
  onUpdate,
  onImmediateUpdate,
  readOnly = false,
  autoFocus = true,
  hideBorder = false,
  ToolbarPlugin = DefaultToolbarPlugin,
  TextEditorWrapper = DefaultTextEditorWrapper,
  EditorInner = DefaultEditorInner,
  innerSx = {},
  omitBlockTypes,
  preserveNewLines = true,
  defaultAutofocusSelection = 'rootEnd',
  allowTableInsert = true,
  showCompactBlockDropdown,
  additionalNodes,
  additionalTransformers,
  additionalPlugins,
  bodyTextColor,
  bodyTextAlignment,
}: TextEditorProps) => {
  const theme = useTextEditorTheme({
    isEditing: !readOnly,
    bodyTextColor,
    bodyTextAlignment,
  });
  const { theme: themeVariant } = useTextEditorContext();
  const [editorStateStr, setEditorStateStr] = useState<string>(initialValue);

  const transformers = useMemo<Transformer[]>(
    () =>
      additionalTransformers
        ? [...additionalTransformers, ...markdownTransformers]
        : markdownTransformers,
    [additionalTransformers]
  );

  // Preserve the original onUpdate contract: emit the initial value on mount
  // and coalesce editor updates through React state. Read onUpdate from a ref
  // so that a new callback identity does not emit the value again. Callers
  // often pass an inline callback that writes to form state, and a repeated
  // emit on each parent render can cause an infinite render loop.
  const onUpdateRef = useLatest(onUpdate);
  useEffect(() => {
    onUpdateRef.current?.(editorStateStr);
  }, [editorStateStr, onUpdateRef]);

  const initialConfig = useMemo(() => {
    return {
      onError(error: unknown) {
        // Handle TableObserver race condition errors that occur when table rows
        // are cut/deleted. This happens because the TableObserver tries to sync
        // with DOM elements that have already been removed. The error doesn't
        // affect editor functionality or data integrity.
        // See: https://github.com/facebook/lexical/blob/main/packages/lexical-table/src/LexicalTableObserver.ts
        if (
          error instanceof Error &&
          (error.message.includes(
            'TableObserver: Expected to find TableElement in DOM'
          ) ||
            error.message.includes('Minified Lexical error #232'))
        ) {
          diagnostics.debug('[TextEditor] TableObserver DOM sync error', {
            error,
          });
          return;
        }
        throw error;
      },
      nodes: [
        HeadingNode,
        ListNode,
        ListItemNode,
        QuoteNode,
        CodeNode,
        CodeHighlightNode,
        TableNode,
        TableCellNode,
        TableRowNode,
        AutoLinkNode,
        LinkNode,
        CitationNode,
        InternalLinkNode,
        FontSizeNode,
        MermaidNode,
        HighlightNode,
        ...(additionalNodes ?? []),
      ],
      namespace: readOnly
        ? 'luminary-text-editor-readonly'
        : 'luminary-text-editor',
      editable: !readOnly,
      theme,
    };
  }, [additionalNodes, readOnly, theme]);

  const handleChange = useCallback(
    (editorState: EditorState, editor: LexicalEditor) => {
      switch (initialValueKind) {
        case 'markdown': {
          const markdown = editorState.read(() => {
            return convertToMarkdownString({
              transformers,
              shouldPreserveNewLines: preserveNewLines,
            });
          });
          onImmediateUpdate?.(markdown);
          setEditorStateStr(markdown);
          break;
        }
        case 'html': {
          const html = editorState.read(() => {
            return $generateHtmlFromNodes(editor);
          });
          onImmediateUpdate?.(html);
          setEditorStateStr(html);
          break;
        }
        case 'json': {
          const json = JSON.stringify(editor.getEditorState());
          onImmediateUpdate?.(json);
          setEditorStateStr(json);
          break;
        }
        default:
          throw new UnreachableError({
            case: initialValueKind,
            message: `Unrecognized format for text editor ${initialValueKind}`,
          });
      }
    },
    [initialValueKind, onImmediateUpdate, preserveNewLines, transformers]
  );

  return (
    <LexicalComposer
      initialConfig={{
        ...initialConfig,
        editorState(editor) {
          switch (initialValueKind) {
            case 'markdown':
              return convertFromMarkdownString({
                markdown: initialValue,
                transformers,
                shouldPreserveNewLines: preserveNewLines,
                shouldMergeAdjacentLines: true,
              });
            case 'html': {
              const parser = new DOMParser();
              const dom = parser.parseFromString(initialValue, 'text/html');
              const nodes = $generateNodesFromDOM(editor, dom);
              $getRoot().select().insertNodes(nodes);
              break;
            }
            case 'json':
              return editor.setEditorState(
                editor.parseEditorState(initialValue || EMPTY_EDITOR_JSON)
              );

            default:
              throw new UnreachableError({
                case: initialValueKind,
                message: `Unrecognized format for text editor ${initialValueKind}`,
              });
          }
        },
      }}
    >
      {readOnly ? (
        <TextEditorWrapper hideBorder={hideBorder}>
          <EditorInner sx={innerSx}>
            <RichTextPlugin
              contentEditable={
                <Box sx={{ height: '100%', overflowY: 'auto' }}>
                  <TextEditorContentEditable themeVariant={themeVariant} />
                </Box>
              }
              placeholder={<></>}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <TablePlugin />
            <LinkPlugin />
            <CopyAllPlugin />
            <CitationPlugin />
            <CitationPastePlugin />
            <MermaidPlugin />
            {additionalPlugins}
            <OnChangePlugin onChange={handleChange} />
          </EditorInner>
        </TextEditorWrapper>
      ) : (
        <TextEditorWrapper>
          <ToolbarPlugin
            textEditorKind={initialValueKind}
            omitBlockTypes={omitBlockTypes}
            allowTableInsert={allowTableInsert}
            showCompactBlockDropdown={showCompactBlockDropdown}
          />
          <EditorInner sx={innerSx}>
            <RichTextPlugin
              contentEditable={
                <Box sx={{ height: '100%', overflowY: 'auto' }}>
                  <TextEditorContentEditable themeVariant={themeVariant} />
                </Box>
              }
              placeholder={<></>}
              ErrorBoundary={LexicalErrorBoundary}
            />
            {autoFocus && (
              <AutoFocusPlugin defaultSelection={defaultAutofocusSelection} />
            )}
            <TablePlugin />
            <TableRowClearPlugin />
            <ListPlugin />
            <CheckListPlugin />
            <CheckListCaretFixPlugin />
            <LinkPlugin />
            <MarkdownShortcutPlugin transformers={transformers} />
            <RootSelectionTabPlugin />
            <TabIndentationPlugin />
            <HistoryPlugin />
            <CitationPlugin />
            <CitationPastePlugin />
            <FontSizePlugin />
            <MermaidPlugin />
            <HighlightPlugin />
            <ContinueStylePlugin />
            {additionalPlugins}
          </EditorInner>
          <OnChangePlugin onChange={handleChange} />
        </TextEditorWrapper>
      )}
    </LexicalComposer>
  );
};
